/* eslint-disable no-empty */
// Silent token refresh + 401/403 recovery.
//
// Problem this solves: the app stores a BIAN `jwtToken` and a ComparerAPI
// `apiToken` in localStorage, both short-lived, with no refresh flow. When they
// expire, API calls fail and the user is forced to log in again. This module
// re-mints both tokens silently using the credentials already saved (encrypted)
// in `oldLoginCrypt`, so the user stays signed in.

import axios from "axios";
import CryptoJS from "crypto-js";
import {
  getUserToken,
  getUserTokenJWT,
  newUserToken,
} from "../api/accounts";

const CRYPT_KEY = process.env.REACT_APP_CRYPTING_KEY;

function decryptBlob(key) {
  const data = localStorage.getItem(key);
  if (!data) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(data, CRYPT_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch {
    return null;
  }
}

// The user's username/password are stored (encrypted) inside oldLoginCrypt at
// login time. We need them to mint a fresh BIAN jwtToken.
export function getStoredUserCredentials() {
  const old = decryptBlob("oldLoginCrypt");
  if (!old || !old.username || !old.password) return null;
  return { username: old.username, password: old.password };
}

// Keep oldLoginCrypt in sync with the refreshed tokens so a page reload
// (automatedLogin) restores fresh tokens instead of stale ones.
function reencryptOldLoginCrypt(updates) {
  const old = decryptBlob("oldLoginCrypt");
  if (!old) return;
  const merged = { ...old, ...updates };
  try {
    const ciphertext = CryptoJS.AES.encrypt(
      JSON.stringify(merged),
      CRYPT_KEY
    ).toString();
    localStorage.setItem("oldLoginCrypt", ciphertext);
  } catch {}
}

// Decode a JWT and check whether it is expired. `skewSeconds` treats a token
// that expires very soon as already expired, so we refresh proactively.
export function isJwtExpired(token, skewSeconds = 30) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload || !payload.exp) return false; // no exp claim -> can't tell
    return payload.exp * 1000 <= Date.now() + skewSeconds * 1000;
  } catch {
    // Opaque / non-JWT token: don't force a refresh here; the 401/403
    // interceptor remains the safety net.
    return false;
  }
}

// Do the actual refresh. Mirrors the token-minting steps in App.handleLogin,
// but reads everything it needs from localStorage so it can run outside React.
async function doRefresh() {
  const creds = getStoredUserCredentials();
  if (!creds) {
    throw new Error("No stored credentials available for silent refresh");
  }

  // 1) Fresh "default" token (uses app-level default credentials).
  const defaultUser = {
    username: process.env.REACT_APP_DEFAULT_USERNAME,
    password: process.env.REACT_APP_DEFAULT_PASSWORD,
  };
  const def = await getUserToken(defaultUser);
  if (!def || !def.status) {
    throw new Error("Failed to refresh default token");
  }
  const defaultToken = def.response.jwtToken;
  localStorage.setItem("defaultToken", defaultToken);

  // 2) Fresh user BIAN jwtToken (this is the one the axios interceptors send).
  const userTok = await getUserTokenJWT(creds, defaultToken);
  if (!userTok || !userTok.status) {
    throw new Error("Failed to refresh user token");
  }
  const jwtToken = userTok.response.jwtToken;
  localStorage.setItem("jwtToken", jwtToken);

  // 3) Fresh ComparerAPI apiToken (best-effort; uses app-level credentials).
  let apiToken;
  try {
    const apiTok = await newUserToken({
      email: process.env.REACT_APP_EMAIL,
      password: process.env.REACT_APP_PASSWORD,
    });
    if (apiTok && apiTok.status) {
      apiToken = apiTok.response.authToken;
      localStorage.setItem("apiToken", apiToken);
    }
  } catch {}

  // 4) Persist refreshed tokens into the encrypted blob used on reload.
  reencryptOldLoginCrypt({ token: jwtToken, newToken: apiToken });

  return { jwtToken, apiToken };
}

// Share a single in-flight refresh so a burst of 401s triggers only one
// re-authentication instead of a stampede.
let refreshPromise = null;
export function refreshAuthTokens() {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Refresh failed (e.g. password changed, account disabled): clear the session
// and send the user to the login page (route "/", same as handleLogout).
export function clearSessionAndRedirect() {
  [
    "jwtToken",
    "oldLoginCrypt",
    "loggedIn",
    "dashboard_status",
    "firstName",
    "userOrganisation",
    "userDetails",
    "apiToken",
  ].forEach((k) => {
    try {
      localStorage.setItem(k, "");
    } catch {}
  });

  const path = window.location.pathname;
  if (path !== "/" && path !== "/login") {
    window.location.assign("/");
  }
}

// Token-minting endpoints. A 401/403 from these means the refresh itself
// failed, so we must NOT try to refresh again (that would loop forever).
function isAuthEndpoint(url) {
  if (!url) return false;
  return (
    url.includes("/Token/authenticate") || // BIAN getUserToken / getUserTokenJWT
    url.includes("/auth/login") // ComparerAPI newUserToken (api-view/auth/login)
  );
}

// ComparerAPI requests authenticate with `apiToken`; everything else (BIAN
// security / model access) uses `jwtToken`. Pick the right one for the retry.
function pickTokenForRequest(req, tokens) {
  const comparerBase = process.env.REACT_APP_COMPARER_API || "";
  const reqUrl = (req && req.url) || "";
  const reqBase = (req && req.baseURL) || "";
  const isComparer =
    !!comparerBase &&
    (reqUrl.startsWith(comparerBase) ||
      reqBase.startsWith(comparerBase) ||
      (reqBase + reqUrl).includes("api-view"));

  return isComparer
    ? tokens.apiToken || localStorage.getItem("apiToken")
    : tokens.jwtToken || localStorage.getItem("jwtToken");
}

// Attach a response interceptor that, on 401/403, silently refreshes the
// tokens once and retries the original request with the correct token. If
// refresh fails, it logs out. Works for both configured instances and the
// shared default axios used by the raw `axios.post(...)` calls in the app.
export function attachAuthRefreshInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error && error.config;
      const status = error && error.response && error.response.status;
      const url = (originalRequest && (originalRequest.url || "")) || "";

      if (
        (status === 401 || status === 403) &&
        originalRequest &&
        !originalRequest._authRetry &&
        !isAuthEndpoint(url)
      ) {
        originalRequest._authRetry = true;
        try {
          const tokens = await refreshAuthTokens();
          const token = pickTokenForRequest(originalRequest, tokens);
          if (token) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          }
        } catch {
          // fall through to logout below
        }
        clearSessionAndRedirect();
      }

      return Promise.reject(error);
    }
  );
}

// Cover every raw `axios.post(...)` / `axios.get(...)` call in the app (most
// ComparerAPI calls use the shared default axios, not a configured instance).
attachAuthRefreshInterceptor(axios);
